import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  cn,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  Spinner,
  Stack,
  toast,
} from 'component-library';
import { TextInput } from 'component-library/TextInput/TextInput';
import React, { FC, HTMLAttributes, PropsWithChildren, useState } from 'react';
import { FormProvider, SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RestApiBadRequestServerError } from '@/api/rest/resources/errors/RestApiBadRequestError';
import {
  PostOrganizationRoadmapNodesRequestBodyData,
  RoadmapNode,
  RoadmapNodeStatus,
} from '@/api/rest/resources/types/organization';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, SuspenseBoundary, useFormField } from '@/components';
import { ControlledCheckbox } from '@/components/Form';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Logger } from '@/lib/logs/logger';

import { useEditRoadmapNode } from '../hooks/useEditRoadmapNode';

export type EditRoadmapNodeProps = {
  organizationId: string;
  roadmapNode?: RoadmapNode;
  parentId?: string | null;
  title?: string;
  onSuccess?: () => void;
  onClose?: () => void;
  showFullForm?: boolean;
} & HTMLAttributes<HTMLDivElement>;

export const AddRoadmap = (props: EditRoadmapNodeProps) => {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState<boolean>(false);

  return (
    <Stack className='w-full gap-6'>
      <Button
        variant='outline'
        className={cn('text-primary-100 w-fit', props.className)}
        onClick={(e) => {
          e.stopPropagation();
          setShowForm(!showForm);
        }}
        leftAdornment={<Icon icon={showForm ? 'ri-subtract-line' : 'ri-add-large-line'} className='w-6' />}
      >
        {props.title ?? t('shared.goals.editNode.addRoadmap')}
      </Button>
      {showForm && <EditRoadmapNode {...props} title={props.title} onClose={() => setShowForm(false)} />}
    </Stack>
  );
};

export const EditRoadmapNode = ({
  organizationId,
  roadmapNode,
  parentId,
  title,
  onSuccess,
  onClose,
  showFullForm,
}: EditRoadmapNodeProps) => {
  const { t } = useTranslation();

  return (
    <Stack className='w-full p-6 border border-neutral-black-12 rounded-2xl' onClick={(e) => e.stopPropagation()}>
      <span className='typography-h3 self-center p-2'>{title ?? t('shared.goals.editNode.editMilestone')}</span>
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <Form
          onSuccess={onSuccess}
          organizationId={organizationId}
          roadmapNode={roadmapNode}
          parentId={parentId}
          showFullForm={showFullForm}
          onClose={onClose}
        />
      </SuspenseBoundary>
    </Stack>
  );
};

export type EditOrganizationRoadmapNodeFields = PostOrganizationRoadmapNodesRequestBodyData;

const Form = ({
  onSuccess,
  onClose,
  organizationId,
  roadmapNode,
  parentId,
  showFullForm = true,
}: {
  organizationId: string;
  roadmapNode?: RoadmapNode;
  parentId?: string | null;
  showFullForm?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const form = useForm<EditOrganizationRoadmapNodeFields>({
    defaultValues: {
      id: roadmapNode?.id || null,
      title: roadmapNode?.title || '',
      description: roadmapNode?.description || '',
      parent: parentId || undefined,
      status: roadmapNode?.status || RoadmapNodeStatus.created,
      visible_to_customer: roadmapNode?.visible_to_customer || false,
    },
  });

  const isFormPristine = !form.formState.isDirty;

  const { submit, isSubmitting } = useEditRoadmapNode();

  const onSubmit: SubmitHandler<EditOrganizationRoadmapNodeFields> = async (inputs) => {
    try {
      const updatedBody = { ...inputs, parent: parentId ?? undefined };
      form.clearErrors();
      await submit({ organizationId, inputs: updatedBody });
      await queryClient.invalidateQueries({
        queryKey: ['organization', { organization_id: organizationId }, 'roadmap-nodes'],
      });
      onSuccess?.();
      form.reset();
    } catch (error) {
      if (error instanceof Error) {
        Logger.error(error);
      }
      if (error instanceof RestApiBadRequestServerError && error.formErrors.length > 0) {
        error.formErrors.forEach((formError) => {
          form.setError(formError.field as keyof EditOrganizationRoadmapNodeFields, {
            type: 'custom',
            message: formError.messages?.[0],
          });
        });
      } else {
        toast({
          title: t('global.ui.toast.errorToastFallbackTitle'),
          description: t('global.ui.toast.errorToastFallbackDescription'),
          type: 'error',
        });
      }
    }
  };

  const roadmapNodeStatusItems: RadioItem[] = Object.keys(RoadmapNodeStatus).map((status) => ({
    value: status,
    label: t(`shared.goals.roadmapNodeStatus.${status as keyof typeof RoadmapNodeStatus}`),
  }));

  return (
    <Stack data-testid='edit-roadmap-node'>
      <FormProvider {...form}>
        <form id='edit-roadmap-node-form' onSubmit={form.handleSubmit(onSubmit)} data-testid='edit-roadmap-node-form'>
          <fieldset disabled={isSubmitting}>
            <Stack className='gap-4'>
              {showFullForm && (
                <ControlledCheckbox
                  id='visible_to_customer'
                  name='visible_to_customer'
                  control={form.control}
                  rules={{
                    required: false,
                  }}
                >
                  <CustomFormLabel>{t('shared.goals.editNode.fields.visible_to_customer.label')}</CustomFormLabel>
                </ControlledCheckbox>
              )}
              {showFullForm && (
                <RadioSelect
                  items={roadmapNodeStatusItems}
                  form={form}
                  fieldName='status'
                  label={t('shared.goals.editNode.fields.status.label')}
                />
              )}
              <TextField
                form={form}
                fieldName='title'
                label={t('shared.goals.editNode.fields.title.label')}
                placeholder={roadmapNode?.title || t('shared.goals.editNode.fields.title.placeholder')}
                required={true}
              />
              <TextField
                form={form}
                fieldName='description'
                label={t('shared.goals.editNode.fields.description.label')}
                placeholder={roadmapNode?.description || t('shared.goals.editNode.fields.description.placeholder')}
                required={true}
              />
            </Stack>
          </fieldset>
        </form>
        <Stack className='flex flex-row justify-end gap-4 pt-6'>
          <Button
            variant='text'
            size='small'
            disabled={isSubmitting}
            onClick={() => {
              form.reset();
              onClose?.();
            }}
          >
            {t('global.ui.buttons.cancel')}
          </Button>
          <Button
            form='edit-roadmap-node-form'
            size='small'
            type='submit'
            disabled={isSubmitting || isFormPristine}
            loading={isSubmitting}
          >
            {t('global.ui.buttons.save')}
          </Button>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

const TextField = ({
  form,
  fieldName,
  label,
  placeholder = '',
  required = false,
}: {
  form: UseFormReturn<EditOrganizationRoadmapNodeFields>;
  fieldName: 'title' | 'description';
  label: string;
  placeholder?: string;
  required?: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <FormField
      control={form.control}
      name={fieldName}
      rules={{ required }}
      render={({ field: { value, ...field } }) => (
        <FormItem className='flex flex-col gap-2'>
          <CustomFormLabel>
            {label}
            {required ? '*' : ''}
          </CustomFormLabel>
          <FormControl>
            <TextInput placeholder={placeholder} value={(value as string) ?? ''} {...field} />
          </FormControl>
          {required && <CustomFormMessage>{t('global.ui.form.input.required')}</CustomFormMessage>}
        </FormItem>
      )}
    />
  );
};

type RadioItem = {
  value: string;
  label: string;
};

const RadioSelect = ({
  form,
  fieldName,
  items,
  label,
  required = false,
}: {
  form: UseFormReturn<EditOrganizationRoadmapNodeFields>;
  fieldName: 'status';
  items: RadioItem[];
  label: string;
  required?: boolean;
}) => {
  const error = form.formState.errors[`${fieldName}`];

  return (
    <FormField
      control={form.control}
      name={fieldName}
      rules={{ required }}
      render={({ field: { ref, value, ...field } }) => (
        <FormItem>
          <CustomFormLabel>
            {label}
            {required ? '*' : ''}
          </CustomFormLabel>
          <Select {...field} value={(value as string) || undefined}>
            <FormControl>
              <SelectTrigger ref={ref} data-testid={`${fieldName}-select`} />
            </FormControl>
            <FormMessage>{error?.message}</FormMessage>
            <SelectContent>
              <SelectGroup>
                {items.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </FormItem>
      )}
    />
  );
};

const CustomFormLabel: FC<PropsWithChildren> = ({ children }) => {
  const { error } = useFormField();

  return (
    <FormLabel
      className={cn('text-[.75rem] font-medium leading-4 text-text-secondary md:text-[.8125rem] md:leading-5', {
        'text-error': error,
      })}
    >
      {children}
    </FormLabel>
  );
};

const CustomFormMessage: FC<PropsWithChildren> = ({ children }) => (
  <FormMessage className='typography-inputLabel mt-0 text-error'>{children}</FormMessage>
);

export const MarkNodeAsDone = ({
  organizationId,
  roadmapNode,
  className,
}: EditRoadmapNodeProps & HTMLAttributes<HTMLButtonElement>) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { submit, isSubmitting } = useEditRoadmapNode();

  const isDone = roadmapNode?.status === RoadmapNodeStatus.closed;

  const handleMarkAsDone = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const status = isDone ? RoadmapNodeStatus.created : RoadmapNodeStatus.closed;
    try {
      const updatedBody = { ...roadmapNode, status };

      await submit({ organizationId, inputs: updatedBody });

      await queryClient.invalidateQueries({
        queryKey: ['organization', { organization_id: organizationId }, 'roadmap-nodes'],
      });
    } catch (error) {
      if (error instanceof Error) Logger.error(error);
      toast({
        title: t('global.ui.toast.errorToastFallbackTitle'),
        description: t('global.ui.toast.errorToastFallbackDescription'),
        type: 'error',
      });
    }
  };

  return (
    <Button
      data-testid='mark-node-as-done-button'
      disabled={isSubmitting}
      onClick={handleMarkAsDone}
      className={cn('w-fit', isDone ? 'bg-primary-100' : '', className)}
      variant={isDone ? 'contained' : 'outline'}
    >
      <Icon icon='ri-checkbox-circle-fill' style={{ color: isDone ? 'text-white-100' : 'text-primary-100' }} />
      {isDone ? t('shared.goals.editNode.editStatus.done') : t('shared.goals.editNode.editStatus.markDone')}
    </Button>
  );
};
